import type { Folder } from '@core/entities/Folder';
import type {
  CreateFolderPayload,
  FolderRepository,
  UpdateFolderPayload,
} from '@core/repositories/FolderRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';

interface FolderResponse {
  id: string;
  collection_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  position: number;
  request_count: number | null;
  child_count: number | null;
  is_favorited: boolean;
  abilities: { view: boolean; update: boolean; delete: boolean };
  created_at: string;
  updated_at: string;
}

const toFolder = (payload: FolderResponse): Folder => ({
  id: payload.id,
  collectionId: payload.collection_id,
  parentId: payload.parent_id ?? null,
  name: payload.name,
  description: payload.description,
  position: payload.position,
  requestCount: payload.request_count,
  childCount: payload.child_count,
  isFavorited: payload.is_favorited,
  abilities: payload.abilities,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

export class FolderRepositoryImpl implements FolderRepository {
  async list(collectionId: string, parentId?: string | null): Promise<Folder[]> {
    try {
      const response = await httpClient.get<{ data: FolderResponse[] }>(
        `/collections/${collectionId}/folders`,
        { params: parentId ? { parent_uuid: parentId } : undefined },
      );
      return response.data.data.map(toFolder);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async get(id: string): Promise<Folder> {
    try {
      const response = await httpClient.get<{ data: FolderResponse }>(`/folders/${id}`);
      return toFolder(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(collectionId: string, payload: CreateFolderPayload): Promise<Folder> {
    try {
      const response = await httpClient.post<{ data: FolderResponse }>(
        `/collections/${collectionId}/folders`,
        { name: payload.name, description: payload.description, parent_uuid: payload.parentId },
      );
      return toFolder(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async update(id: string, payload: UpdateFolderPayload): Promise<Folder> {
    try {
      const response = await httpClient.put<{ data: FolderResponse }>(`/folders/${id}`, payload);
      return toFolder(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async move(id: string, parentId: string | null, position: number): Promise<Folder> {
    try {
      const response = await httpClient.post<{ data: FolderResponse }>(`/folders/${id}/move`, {
        parent_uuid: parentId,
        position,
      });
      return toFolder(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/folders/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async favorite(id: string): Promise<void> {
    try {
      await httpClient.post(`/folders/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async unfavorite(id: string): Promise<void> {
    try {
      await httpClient.delete(`/folders/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
